import React, { useMemo, useState } from 'react';
import LoadingSpinner from '../Components/LoadingSpinner';
import axios from 'axios';

const defaultParams = {
  location: '',
  page: '5',
  sortOrder: 'Homes_for_you',
  listingStatus: 'For_Sale',
  bed_min: 'No_Min',
  bed_max: 'No_Max',
  bathrooms: 'Any',
  homeType: 'Houses, Townhomes, Multi-family, Condos/Co-ops, Lots-Land, Apartments, Manufactured',
  maxHOA: 'Any',
  listingType: 'By_Agent',
  listingTypeOptions: 'Agent listed,New Construction,Fore-closures,Auctions',
  parkingSpots: 'Any',
  mustHaveBasement: 'No',
  daysOnZillow: 'Any',
  soldInLast: 'Any',
};

const Section = ({ title, children, right }) => (
  <div className="mb-4">
    <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap">
      <h5 className="mb-2">{title}</h5>
      {right}
    </div>
    {children}
  </div>
);

const KeyValue = ({ label, value, suffix }) => (
  <div className="col-6 col-md-3 mb-3">
    <div className="text-muted small">{label}</div>
    <div className="fw-semibold">{value != null && value !== '' ? `${value}${suffix || ''}` : '—'}</div>
  </div>
);

function PropertySearch() {
  const [params, setParams] = useState(defaultParams);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const handleInput = (e) => {
    const { name, value } = e.target;
    setParams((prev) => ({ ...prev, [name]: value }));
  };

  const search = async () => {
    setError('');
    setLoading(true);
    setData(null);
    setCandidates([]);
    setSelectedIdx(0);
    setDetails(null);
    try {
      const key = process.env.REACT_APP_RAPIDAPI_KEY;
      if (!key) throw new Error('Missing RapidAPI key. Set REACT_APP_RAPIDAPI_KEY in .env');

      const resp = await axios.request({
        method: 'GET',
        url: 'real-estate101.p.rapidapi.com',
        params,
        headers: {
          'x-rapidapi-key': key,
          'x-rapidapi-host': 'zillow-working-api.p.rapidapi.com',
        },
      });
      const payload = resp.data || {};
      setData(payload);
      const cands = buildCandidates(payload);
      setCandidates(cands);
      if (cands.length === 1) {
        setSelectedIdx(0);
        setDetails(payload);
      }
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  const sr = data?.searchResults || null;
  console.log("Search results:", sr);
  console.log("Full data:", data);
  const active = details || data;
  const asr = active?.searchResults || null;
  const addr = sr?.address || {
    streetAddress: sr?.streetAddress,
    city: sr?.city,
    state: sr?.state,
    zipcode: sr?.zipcode,
  };
  const reso = (asr?.resoFacts) || {};

  const photos = useMemo(() => {
    const p = data?.originalPhotos || [];
    if (Array.isArray(p) && p.length) {
      return p.map((ph) => ph?.mixedSources?.jpeg?.[0]?.url || ph?.mixedSources?.webp?.[0]?.url).filter(Boolean);
    }
    const thumbs = data?.thumb || [];
    return thumbs.map((t) => t?.url).filter(Boolean);
  }, [data]);

  const formatCurrency = (n) => (n || n === 0 ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) : '—');
  const formatNumber = (n) => (n || n === 0 ? n.toLocaleString('en-US') : '—');

  const deriveLot = () => {
    const lotAreaValue = active?.lotAreaValue || asr?.lotAreaValue;
    const lotUnits = (active?.lotAreaUnits || asr?.lotAreaUnits || '').toLowerCase();
    if (lotAreaValue && lotUnits.includes('square')) {
      return `${(lotAreaValue / 43560).toFixed(2)} acres`;
    }
    if (lotAreaValue && lotUnits.includes('acre')) {
      return `${lotAreaValue} acres`;
    }
    return reso?.lotSize || (active?.lotSize ? `${formatNumber(active.lotSize)} sqft` : '—');
  };

  // Build a list of candidate properties for selection when multiple results exist
  function buildCandidates(payload) {
    const list = [];
    const primary = payload?.searchResults;
    if (primary) list.push(toCandidate(primary));
    const mods = payload?.collections?.modules || [];
    mods.forEach((m) => {
      (m?.propertyDetails || []).forEach((p) => list.push(toCandidate(p)));
    });
    const results = payload?.results || payload?.properties;
    if (Array.isArray(results)) {
      results.forEach((p) => list.push(toCandidate(p)));
    }
    const seen = new Set();
    return list.filter((c) => {
      const key = `${c.zpid || ''}|${c.fullAddress}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  function toCandidate(p) {
    const address = p?.address || p;
    const street = address?.streetAddress || p?.streetAddress || '';
    const city = address?.city || p?.city || '';
    const state = address?.state || p?.state || '';
    const zipcode = address?.zipcode || p?.zipcode || '';
    const fullAddress = [street, city, state, zipcode].filter(Boolean).join(', ');
    return {
      zpid: p?.zpid,
      fullAddress,
      streetAddress: street,
      city,
      state,
      zipcode,
      price: p?.price,
      bedrooms: p?.bedrooms ?? p?.resoFacts?.bedrooms,
      bathrooms: p?.bathrooms ?? p?.resoFacts?.bathroomsFloat ?? p?.resoFacts?.bathrooms,
      livingArea: p?.livingArea || p?.livingAreaValue || p?.resoFacts?.livingArea,
      hdpUrl: p?.hdpUrl,
    };
  }

  const onSelectCandidate = async (idx) => {
    setSelectedIdx(idx);
    const cand = candidates[idx];
    if (!cand) return;
    if (idx === 0 && data?.searchResults) {
      setDetails(data);
      return;
    }
    const location = [cand.streetAddress, cand.city, cand.state, cand.zipcode].filter(Boolean).join(', ');
    if (!location) return;
    setDetailsLoading(true);
    try {
      const key = process.env.REACT_APP_RAPIDAPI_KEY;
      const resp = await axios.request({
        method: 'GET',
        url: 'real-estate101.p.rapidapi.com',
        params: { ...defaultParams, location },
        headers: {
          'x-rapidapi-key': key,
          'x-rapidapi-host': 'zillow-working-api.p.rapidapi.com',
        },
      });
      setDetails(resp.data || {});
    } catch (e) {
      console.error('Detail fetch failed', e);
    } finally {
      setDetailsLoading(false);
    }
  };

  return (
    <div className="container mt-4">
      <div className="card p-4 shadow-sm mb-4">
        <h4 className="mb-3">Property Search</h4>
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label">Location</label>
            <input
              name="location"
              className="form-control"
              placeholder="EX: 123 Main St, City, State, Zip"
              value={params.location}
              onChange={handleInput}
            />
          </div>
          {/* <div className="col-md-4">
            <label className="form-label">Listing Status</label>
            <select name="listingStatus" className="form-select" value={params.listingStatus} onChange={handleInput}>
              <option value="For_Sale">For Sale</option>
              <option value="For_Rent">For Rent</option>
              <option value="Sold">Sold</option>
            </select>
          </div> */}
          <div className="col-md-2 d-grid">
            <label className="form-label d-none d-md-block">&nbsp;</label>
            <button className="btn btn-primary" onClick={search} disabled={loading || !params.location}>Search</button>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {loading && <LoadingSpinner message="Searching properties…" />}

      {sr && (
        <div className="card p-4 shadow-sm mb-4">
          {candidates.length > 1 && (
            <Section title="Results">
              <div className="table-responsive">
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th></th>
                      <th>Address</th>
                      <th className="text-end">Price</th>
                      <th className="text-center">Beds</th>
                      <th className="text-center">Baths</th>
                      <th className="text-end">Sqft</th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidates.map((c, i) => (
                      <tr key={`${c.zpid || i}-${c.fullAddress}`}>
                        <td style={{ width: 36 }}>
                          <input
                            type="radio"
                            name="cand"
                            className="form-check-input"
                            checked={selectedIdx === i}
                            onChange={() => onSelectCandidate(i)}
                          />
                        </td>
                        <td>{c.fullAddress || '—'}</td>
                        <td className="text-end">{c.price != null ? formatCurrency(c.price) : '—'}</td>
                        <td className="text-center">{c.bedrooms ?? '—'}</td>
                        <td className="text-center">{c.bathrooms ?? '—'}</td>
                        <td className="text-end">{c.livingArea ? formatNumber(Number(c.livingArea)) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {detailsLoading && <LoadingSpinner className="mt-2" message="Loading selected property…" />}
            </Section>
          )}
          <Section
            title="Overview"
            right={<span className="text-muted small">Status: {data?.homeStatus || '—'}</span>}
          >
            <div className="mb-3">
              <h5 className="mb-0">{addr?.streetAddress || '—'}</h5>
              <div className="text-muted">
                {[addr?.city, addr?.state, addr?.zipcode].filter(Boolean).join(', ') || '—'}
              </div>
            </div>
            <div className="row">
              <KeyValue label="Price" value={formatCurrency(sr?.price || data?.zestimate)} />
              <KeyValue label="Estimate" value={formatCurrency(sr?.zestimate || data?.zestimate)} />
              <KeyValue label="Rent Estimate" value={formatCurrency(sr?.rentZestimate || data?.rentZestimate)} />
              <KeyValue label="Type" value={(sr?.homeType || reso?.homeType || data?.homeType || '').replaceAll('_',' ')} />
            </div>
            {data?.desktopWebHdpImageLink && (
              <div className="mt-2">
                <a
                  href={data?.hiResImageLink || data.desktopWebHdpImageLink}
                  target="_blank"
                  rel="noreferrer"
                  title="Open full-size image"
                >
                  <img
                    src={data.desktopWebHdpImageLink}
                    alt="Primary"
                    className="img-fluid rounded"
                    style={{ maxWidth: '360px' }}
                  />
                </a>
              </div>
            )}
          </Section>

          <Section title="Key Facts">
            <div className="row">
              <KeyValue label="Bedrooms" value={sr?.bedrooms ?? reso?.bedrooms} />
              <KeyValue label="Bathrooms" value={sr?.bathrooms ?? reso?.bathroomsFloat ?? reso?.bathrooms} />
              <KeyValue label="Living Area" value={reso?.livingArea || (data?.livingArea ? `${formatNumber(data.livingArea)} sqft` : '—')} />
              <KeyValue label="Lot Size" value={deriveLot()} />
              <KeyValue label="Year Built" value={sr?.yearBuilt ?? reso?.yearBuilt ?? data?.yearBuilt} />
              <KeyValue label="Price/Sqft" value={reso?.pricePerSquareFoot ?? data?.pricePerSquareFoot} />
              <KeyValue label="Parcel #" value={reso?.parcelNumber || data?.parcelId} />
              <KeyValue label="Zoning" value={reso?.zoning || data?.zoning} />
            </div>
          </Section>

          {photos.length > 0 && (
            <Section title="Photos">
              <div className="row g-2">
                {photos.slice(0, 12).map((url, i) => (
                  <div className="col-6 col-md-3" key={i}>
                    <img src={url} alt={`photo-${i}`} className="img-fluid rounded" />
                  </div>
                ))}
              </div>
            </Section>
          )}

          {Array.isArray(data?.schools) && data.schools.length > 0 && (
            <Section title="Schools">
              <div className="row">
                {data.schools.map((s, idx) => (
                  <div className="col-md-4 mb-2" key={idx}>
                    <div className="fw-semibold">{s.name}</div>
                    <div className="text-muted small">
                      {s.level} • Rating: {s.rating ?? '—'} • {s.distance ? `${s.distance} mi` : ''}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {Array.isArray(sr?.priceHistory) && sr.priceHistory.length > 0 && (
            <Section title="Price History">
              <ul className="list-unstyled mb-0">
                {sr.priceHistory.slice(0, 10).map((ph, idx) => (
                  <li key={idx} className="d-flex justify-content-between border-bottom py-1">
                    <span className="text-muted">{ph.date} — {ph.event}</span>
                    <span>{ph.price != null ? formatCurrency(ph.price) : '—'}</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {Array.isArray(reso?.atAGlanceFacts) && reso.atAGlanceFacts.length > 0 && (
            <Section title="At A Glance">
              <div className="row">
                {reso.atAGlanceFacts
                  .filter((f) => (f?.factLabel || '').toLowerCase() !== 'offer review date')
                  .map((f, idx) => (
                    <KeyValue key={idx} label={f.factLabel} value={f.factValue} />
                  ))}
              </div>
            </Section>
          )}

          {Array.isArray(reso?.feesAndDues) && reso.feesAndDues.length > 0 && (
            <Section title="Fees & Dues">
              <div className="row">
                {reso.feesAndDues.map((f, idx) => (
                  <KeyValue key={idx} label={f.type || 'Fee'} value={f.fee} />
                ))}
              </div>
            </Section>
          )}
           {sr?.description && (
            <Section title="Description">
              <p>{sr.description}</p>
            </Section>
          )}
        </div>
      )}
    </div>
  );
}

export default PropertySearch;
